/**
 * Pozycja oferty — wiersz, który SPRZEDAJE, nie tylko wycenia.
 *
 * Zwinięty: nazwa, którą klient rozpoznaje, pakiet, tryb, ilość × cena i
 * wartość — bez tabeli, więc bez poziomego przewijania na telefonie (na
 * zrzutach właściciela z 03.09.2026 tabela ucinała kolumnę z ceną).
 *
 * Rozwinięty, w stałej kolejności: zdanie korzyści → korzyści → co dostajesz
 * w tym pakiecie → jak to działa w praktyce → zrzuty i filmy → pytania →
 * dla kogo. Sekcja bez treści znika; gdy karty nie ma wcale, zostaje jedno
 * uczciwe zdanie odsyłające do partnera — nigdy pusta ramka.
 *
 * `tryb='podglad'` (edytor super-admina) różni się jednym: puste sekcje
 * pokazują szarą podpowiedź, co dopisać. Komponent nie wie, czy oferta
 * istnieje — dostaje dane i renderuje.
 */

import {
  Boxes,
  Check,
  CheckCircle2,
  Layers,
  MonitorSmartphone,
  Package,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import {
  ETYKIETY_TRYBU,
  formatujKwote,
  listaFunkcji,
  maSzczegoly,
  nazwaDlaKlienta,
  type PozycjaOfertyDto,
} from '../../lib/oferta/karta';

import { GaleriaMediow } from './GaleriaMediow';
import { PytaniaIOdpowiedzi } from './PytaniaIOdpowiedzi';
import { Rozwijak } from './Rozwijak';

export interface PozycjaOfertyProps {
  id: string;
  pozycja: PozycjaOfertyDto;
  waluta: string;
  otwarta: boolean;
  onToggle: () => void;
  tryb?: 'klient' | 'podglad';
  /** Nazwa partnera do zdania „szczegóły omówi z Tobą …”. */
  partner?: string | null;
  /** Podgląd treści bez cen (edytor karty). */
  pokazKwoty?: boolean;
  bazaProjektu?: string;
}

const IKONY = {
  plan: Package,
  modul: Boxes,
  licencja: MonitorSmartphone,
} as const;

export function PozycjaOferty({
  id,
  pozycja,
  waluta,
  otwarta,
  onToggle,
  tryb = 'klient',
  partner,
  pokazKwoty = true,
  bazaProjektu,
}: PozycjaOfertyProps) {
  const karta = pozycja.karta ?? null;
  const nazwa = nazwaDlaKlienta(pozycja);
  const nazwaTechniczna = nazwa !== pozycja.nazwa ? pozycja.nazwa : null;
  const Ikona = IKONY[pozycja.typ] ?? Boxes;
  const podglad = tryb === 'podglad';
  const funkcje = listaFunkcji(pozycja);
  const maCoRozwinac = podglad || maSzczegoly(pozycja);
  const ilosc = Number(pozycja.ilosc) || 1;

  const naglowek = (
    <div className="flex items-start gap-3 min-w-0">
      <span
        aria-hidden="true"
        className="mt-0.5 w-9 h-9 shrink-0 rounded-ids-md bg-aurora-brand/15 text-aurora-brand grid place-items-center"
      >
        <Ikona className="w-5 h-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-aurora-text leading-snug line-clamp-2">
          {nazwa}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-aurora-subtle">
          {pozycja.pakiet && (
            <span className="badge badge--brand">
              Pakiet {pozycja.pakiet.nazwa}
              {pozycja.pakiet.odznaka && (
                <span className="opacity-80">· {pozycja.pakiet.odznaka}</span>
              )}
            </span>
          )}
          <span>{ETYKIETY_TRYBU[pozycja.tryb] ?? pozycja.tryb}</span>
        </p>
        {nazwaTechniczna && (
          <p className="mt-0.5 text-[11px] text-aurora-subtle">w cenniku: {nazwaTechniczna}</p>
        )}
        {pokazKwoty && (
          <div className="mt-2 flex items-baseline justify-between gap-3 md:hidden">
            <span className="mono tabular-nums text-[13px] text-aurora-muted">
              {ilosc > 1 ? `${ilosc} × ${formatujKwote(pozycja.cena_jednostkowa, waluta)}` : ''}
            </span>
            <span className="mono tabular-nums text-[15px] font-semibold text-aurora-text whitespace-nowrap">
              {formatujKwote(pozycja.kwota, waluta)}
            </span>
          </div>
        )}
      </div>
      {pokazKwoty && (
        <div className="hidden md:flex items-baseline gap-6 shrink-0 pt-1">
          <span className="mono tabular-nums text-[13px] text-aurora-muted min-w-[120px] text-right">
            {ilosc > 1 ? `${ilosc} × ${formatujKwote(pozycja.cena_jednostkowa, waluta)}` : ''}
          </span>
          <span className="mono tabular-nums text-[15px] font-semibold text-aurora-text min-w-[120px] text-right whitespace-nowrap">
            {formatujKwote(pozycja.kwota, waluta)}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <article className="py-4 first:pt-0 last:pb-0" data-typ={pozycja.typ}>
      <Rozwijak
        id={id}
        otwarty={otwarta}
        onToggle={onToggle}
        chevron={maCoRozwinac}
        naglowek={naglowek}
        klasaPrzycisku="py-1 px-1 -mx-1 hover:bg-aurora-bg-subtle/60 transition-colors"
        etykietaAria={`${otwarta ? 'Zwiń' : 'Rozwiń'}: ${nazwa}`}
      >
        <div className="mt-3 ml-0 sm:ml-12 rounded-ids-lg bg-aurora-bg-subtle border border-aurora-border p-4 space-y-5">
          {!karta && funkcje.length === 0 && !podglad && (
            <p className="text-sm text-aurora-muted">
              Szczegóły tej pozycji omówi z Tobą {partner?.trim() || 'partner Insolitum'}.
            </p>
          )}

          {(karta?.naglowek || karta?.opis || podglad) && (
            <Sekcja
              podglad={podglad}
              pusta={!karta?.naglowek && !karta?.opis}
              podpowiedz="Dodaj jedno zdanie korzyści i 2–4 zdania opisu — to pierwsza rzecz, którą klient przeczyta."
            >
              {karta?.naglowek && (
                <p className="text-[17px] font-semibold text-aurora-text leading-snug">
                  {karta.naglowek}
                </p>
              )}
              {karta?.opis && (
                <p className="mt-1.5 text-sm text-aurora-muted leading-relaxed whitespace-pre-line">
                  {karta.opis}
                </p>
              )}
            </Sekcja>
          )}

          {(Boolean(karta?.korzysci?.length) || podglad) && (
            <Sekcja
              tytul="Co zyskujesz"
              podglad={podglad}
              pusta={!karta?.korzysci?.length}
              podpowiedz="Dodaj 3–5 korzyści — klient zobaczy je jako kafelki."
            >
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(karta?.korzysci ?? []).slice(0, 5).map((k, i) => (
                  <li
                    key={`${k.tytul}-${i}`}
                    className="flex items-start gap-2.5 rounded-ids-md border border-aurora-border bg-aurora-surface p-3"
                  >
                    <CheckCircle2
                      aria-hidden="true"
                      className="w-4 h-4 mt-0.5 shrink-0 text-aurora-success"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-aurora-text leading-snug">
                        {k.tytul}
                      </p>
                      {k.opis && (
                        <p className="mt-0.5 text-[13px] text-aurora-muted leading-relaxed">
                          {k.opis}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Sekcja>
          )}

          {(funkcje.length > 0 || karta?.wariant?.naglowek || podglad) && (
            <Sekcja
              tytul="Co dostajesz w tym pakiecie"
              podglad={podglad}
              pusta={funkcje.length === 0 && !karta?.wariant?.naglowek}
              podpowiedz="Wypisz konkretne rzeczy, które klient dostaje — bez ogólników."
            >
              {karta?.wariant?.naglowek && (
                <p className="mb-2 flex items-start gap-2 text-sm text-aurora-text">
                  <Layers
                    aria-hidden="true"
                    className="w-4 h-4 mt-0.5 shrink-0 text-aurora-brand"
                  />
                  <span>{karta.wariant.naglowek}</span>
                </p>
              )}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {funkcje.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-[13.5px] text-aurora-text leading-relaxed"
                  >
                    <Check
                      aria-hidden="true"
                      className="w-4 h-4 mt-0.5 shrink-0 text-aurora-success"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </Sekcja>
          )}

          {(Boolean(karta?.jak_dziala?.length) || podglad) && (
            <Sekcja
              tytul="Jak to działa w praktyce"
              podglad={podglad}
              pusta={!karta?.jak_dziala?.length}
              podpowiedz="Opisz 3–5 kroków pierwszego tygodnia — klient ma zobaczyć siebie w tej historii."
            >
              <ol className="space-y-2.5">
                {(karta?.jak_dziala ?? []).slice(0, 6).map((k, i) => (
                  <li key={`${k.krok}-${i}`} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="w-6 h-6 shrink-0 rounded-full bg-aurora-brand/15 text-aurora-brand text-xs font-bold grid place-items-center"
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-aurora-text leading-snug">
                        {k.krok}
                      </p>
                      {k.opis && (
                        <p className="text-[13px] text-aurora-muted leading-relaxed">{k.opis}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </Sekcja>
          )}

          {(Boolean(karta?.media?.length) || podglad) && (
            <Sekcja
              tytul="Zobacz na ekranie"
              podglad={podglad}
              pusta={!karta?.media?.length}
              podpowiedz="Dodaj zrzuty ekranu albo krótki film — klient chce zobaczyć, co kupuje."
            >
              <GaleriaMediow
                media={karta?.media ?? []}
                nazwaPozycji={nazwa}
                bazaProjektu={bazaProjektu}
              />
            </Sekcja>
          )}

          {(Boolean(karta?.faq?.length) || podglad) && (
            <Sekcja
              tytul="Najczęstsze pytania"
              podglad={podglad}
              pusta={!karta?.faq?.length}
              podpowiedz="Dodaj 2–4 pytania, które klienci zadają przy podpisie."
            >
              <PytaniaIOdpowiedzi id={id} pytania={karta?.faq ?? []} />
            </Sekcja>
          )}

          {(karta?.dla_kogo || podglad) && (
            <Sekcja
              podglad={podglad}
              pusta={!karta?.dla_kogo}
              podpowiedz="Napisz, dla kogo ta pozycja jest — klient ma potwierdzić: to o mnie."
            >
              <p className="flex items-start gap-2 text-[13px] text-aurora-muted leading-relaxed">
                <Users aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <span className="font-semibold text-aurora-text">Dla kogo: </span>
                  {karta?.dla_kogo}
                </span>
              </p>
            </Sekcja>
          )}
        </div>
      </Rozwijak>
    </article>
  );
}

function Sekcja({
  tytul,
  children,
  podglad,
  pusta,
  podpowiedz,
}: {
  tytul?: string;
  children: ReactNode;
  podglad: boolean;
  pusta: boolean;
  podpowiedz: string;
}) {
  if (pusta && !podglad) return null;
  return (
    <section>
      {tytul && <h3 className="overline mb-2">{tytul}</h3>}
      {pusta ? (
        <p className="text-xs text-aurora-subtle italic border border-dashed border-aurora-border rounded-ids-md p-3">
          {podpowiedz}
        </p>
      ) : (
        children
      )}
    </section>
  );
}
